/**
 * Little wrapper around _.sortBy that sorts by any number of keys, in lex order.
 *
 * keyAccessors must be an array, and each element must be one of:
 * (1) The string name of the property to access.
 * (2) A function from element to (string | number). Unlike Array.prototype.sort, numbers are
 *     sorted in numeric order (even without naturalSort), not coerced to strings first.
 * (3) An object {
 *   -- reversed: true|false
 *   -- naturalSort: true|false
 *   -- accessor: (1) or (2)
 *        - note: if (2), the return type must be string to use naturalSort.
 * }
 */
import {naturalSortByFunction, naturalSortByKey} from '@benchling/js-utils';
import _ from 'underscore';

type AccessorObject<T> =
  | {
      reversed?: boolean;
      naturalSort: true;
      // if naturalSorting, accessor functions must return strings
      accessor: (keyof T & string) | ((obj: T) => string);
    }
  | {
      reversed?: boolean;
      naturalSort?: false;
      accessor: (keyof T & string) | ((obj: T) => string | number);
    };

export type KeyAccessor<T> = (keyof T & string) | ((obj: T) => string | number) | AccessorObject<T>;

function sortByAccessorObject<T>(array: Array<T>, keyAccessor: AccessorObject<T>): Array<T> {
  let result = array;
  const reversed = Boolean(keyAccessor.reversed);
  if (reversed) {
    result = result.reverse();
  }
  if (keyAccessor.naturalSort) {
    if (_.isFunction(keyAccessor.accessor)) {
      result = naturalSortByFunction(result, keyAccessor.accessor);
    } else {
      result = naturalSortByKey(result, keyAccessor.accessor);
    }
  } else if (_.isFunction(keyAccessor.accessor)) {
    // The type of _.sortBy is overloaded to take either a string or a _.ListIterator.
    // Without this conditional, the version that takes a string is always used.
    result = _.sortBy(result, keyAccessor.accessor);
  } else {
    result = _.sortBy(result, keyAccessor.accessor);
  }
  if (reversed) {
    result = result.reverse();
  }
  return result;
}

export default function sortedBy<T>(array: Array<T>, keyAccessors: Array<KeyAccessor<T>>): Array<T> {
  let result = array.slice();
  for (const keyAccessor of keyAccessors.slice().reverse()) {
    if (typeof keyAccessor === 'object') {
      result = sortByAccessorObject(result, keyAccessor);
    } else if (_.isFunction(keyAccessor)) {
      // The type of _.sortBy is overloaded to take either a string or a _.ListIterator.
      // Without this conditional, the version that takes a string is always used.
      result = _.sortBy(result, keyAccessor);
    } else {
      result = _.sortBy(result, keyAccessor);
    }
  }
  return result;
}
